import { inject } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { FeedbackService } from "../../services/feedback.service";
import * as FeedbackActions from './feedback.actions';
import { catchError, map, mergeMap, of } from "rxjs";
import { NotificationService } from "../../services/notification.service";
import { TranslateService } from "@ngx-translate/core";

export const loadFeedbacks$ = createEffect(
  (
    actions$ = inject(Actions),
    feedbackService = inject(FeedbackService),
    notification = inject(NotificationService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(FeedbackActions.loadFeedbacks),
      mergeMap(() =>
        feedbackService.getFeedbacks().pipe(
          map(feedbacks => {
            // Optional:
            // notification.success('Feedbacks loaded.');
            return FeedbackActions.loadFeedbacksSuccess({ feedbacks });
          }),
          catchError(error => {
            notification.error(translate.instant('feedback.notifications.loadFailure'));
            return of(FeedbackActions.loadFeedbacksFailure({ error }));
          })
        )
      )
    ),
  { functional: true }
);

export const createFeedback$ = createEffect(
  (
    actions$ = inject(Actions),
    feedbackService = inject(FeedbackService),
    notification = inject(NotificationService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(FeedbackActions.createFeedback),
      mergeMap(({ feedback }) =>
        feedbackService.createFeedback(feedback).pipe(
          map(created => {
            notification.success(translate.instant('feedback.notifications.createSuccess'));
            return FeedbackActions.createFeedbackSuccess({ feedback: created });
          }),
          catchError(error => {
            notification.error(translate.instant('feedback.notifications.createFailure'));
            return of(FeedbackActions.createFeedbackFailure({ error }));
          })
        )
      )
    ),
  { functional: true }
);

export const updateFeedback$ = createEffect(
  (
    actions$ = inject(Actions),
    feedbackService = inject(FeedbackService),
    notification = inject(NotificationService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(FeedbackActions.updateFeedback),
      mergeMap(({ id, updates }) =>
        feedbackService.updateFeedback(id, updates).pipe(
          map((feedback) => {
            notification.success(translate.instant('feedback.notifications.updateSuccess'));
            return FeedbackActions.updateFeedbackSuccess({ feedback });
          }),
          catchError((error) => {
            notification.error(translate.instant('feedback.notifications.updateFailure'));
            return of(FeedbackActions.updateFeedbackFailure({ error }));
          })
        )
      )
    ),
  { functional: true }
);

export const deleteFeedback$ = createEffect(
  (
    actions$ = inject(Actions),
    feedbackService = inject(FeedbackService),
    notification = inject(NotificationService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(FeedbackActions.deleteFeedback),
      mergeMap(({ id }) =>
        feedbackService.deleteFeedback(id).pipe(
          map(() => {
            notification.success(translate.instant('feedback.notifications.deleteSuccess'));
            return FeedbackActions.deleteFeedbackSuccess({ id });
          }),
          catchError(error => {
            notification.error(translate.instant('feedback.notifications.deleteFailure'));
            return of(FeedbackActions.deleteFeedbackFailure({ error }));
          })
        )
      )
    ),
  { functional: true }
);

export const FeedbackEffects = {
    loadFeedbacks$,
    createFeedback$,
    updateFeedback$,
    deleteFeedback$
};